import * as Lockr from 'lockr';
import isEqual from 'lodash/isEqual';

import { AppName } from '../Constants';
import { safelyParseJSON } from '../Json';
import { SyncExternalStore } from '../SyncExternalStore';
import { captureException } from '../errors/Error';

import { hasAppApi } from '@egr/xbox/app-api/Communication';

import { ListenableEvent } from '@easterngraphics/wcf/modules/utils';

const reload = new ListenableEvent<void, Window>(window);

export class PersistentStore<State extends {}> extends SyncExternalStore<State> {
    public static async loadOnStart(deprecatedKey: string = 'app-settings'): Promise<void> {
        if (hasAppApi) {
            try {
                const { get, set } = await (await import('./PersistentStorageApp')).createStorage();
                PersistentStore.get = get;
                PersistentStore.set = set;
                PersistentStore.delete = () => { /* TODO */ };
                PersistentStore.savePermitted = true;
                reload.trigger();
            } catch (error) {
                captureException(error, 'debug');
            }
        } else {
            const value: string | null = Lockr.get(deprecatedKey);
            if (value != null) {
                Lockr.rm(deprecatedKey);
            }
        }
    }

    private static get(name: string): unknown {
        try {
            const value = localStorage.getItem(`${AppName}.${name}`);
            if (value == null) {
                return {};
            }
            return safelyParseJSON(value) ?? {};
        } catch (e) {
            captureException(e, 'debug');
            return {};
        }
    }

    private static delete(name: string): void {
        try {
            localStorage.removeItem(`${AppName}.${name}`);
        } catch (e) {
            captureException(e, 'debug');
        }
    }

    public static deleteDeprecated(name: string): void {
        try {
            localStorage.removeItem(name);
        } catch (e) {
            captureException(e, 'debug');
        }
    }

    private static set(name: string, state: unknown): void {
        localStorage.setItem(`${AppName}.${name}`, JSON.stringify(state));
    }

    private static savePermitted = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    protected static stores = new Map<string, PersistentStore<any>>();
    /**
     * should be called after user submits cookie-dialog
     */
    public static enableSaving() {
        // avoid unnecessary settings writes during initial load (if app api is available)
        if (this.savePermitted) {
            return;
        }

        this.savePermitted = true;
        for (const store of this.stores.values()) {
            const content = store.getSaveContent();
            if (Object.keys(content).length) {
                const { restored, ...otherState } = store.snapshot;
                let requireSave = false;
                // will not override/remove deprecated untracked properties of store state
                if (restored && store.saveKeys != null) {
                    const keys = store.saveKeys.length ? store.saveKeys : Object.keys(otherState);
                    for (const key of keys) {
                        if (!isEqual(otherState[key], content[key as string])) {
                            requireSave = true;
                            break;
                        }
                    }
                }

                if (requireSave || !restored) {
                    void PersistentStore.set(store.name, content);
                }
            }
        }
    }

    protected get savePermitted(): boolean {
        return PersistentStore.savePermitted || this._savePermitted;
    }

    protected onReloadTrigger = () => {
        reload.removeListener(this.onReloadTrigger);
        let restoredState: State | undefined;

        const storage = PersistentStore.get(this.name) ?? {};
        if (
            Object.keys(storage).length
        ) {
            restoredState = {
                ...this.initialDefaultState,
                ...storage,
                restored: 'localStorage',
            };
        }
        this.updateState({ ...this.initialState, ...restoredState });
    };

    constructor(
        protected name: string,
        protected initialDefaultState: State,
        protected saveKeys: Array<keyof State> | null = [],
        protected _savePermitted: boolean = false,
        disableStore: boolean = false
    ) {
        if (PersistentStore.stores.get(name)) {
            console.warn(`localStorage item ${name} already used`);
        }

        let restoredState: State | undefined;

        if (!disableStore) {
            const storage = PersistentStore.get(name) ?? {};
            if (Object.keys(storage).length) {
                restoredState = {
                    ...initialDefaultState,
                    ...storage,
                    restored: 'localStorage',
                };
            }
        }
        super(initialDefaultState, restoredState);
        if (disableStore) {
            return;
        }
        reload.addListener(this.onReloadTrigger);
        this.getSaveContent = this.getSaveContent.bind(this);

        this.subscribe(
            (value) => {
                if (
                    this.savePermitted &&
                    this.saveKeys != null && (
                        this.saveKeys.length === 0 ||
                        value.find((key) => this.saveKeys?.includes(key)) != null
                    )) {
                    const content = this.getSaveContent();
                    if (Object.keys(content).length) {
                        void PersistentStore.set(name, content);
                    } else {
                        void PersistentStore.delete(name);
                    }
                }
            }
        );

        PersistentStore.stores.set(this.name, this);
    }

    private getSaveContent(): Partial<State> {
        const { restored, ...state } = this.snapshot;

        if (this.saveKeys == null) {
            return {};
        }

        if (this.saveKeys.length === 0) {
            return state as Partial<State>;
        }

        const content = {};

        for (const key of Object.keys(state)) {
            if (
                this.saveKeys.includes(key as keyof State) && (
                    state[key] !== this.initialState[key] ||
                    this.internalState.restored
                )
            ) {
                content[key] = state[key];
            }
        }
        return content;
    }
}
